import { KgInsightThemeType } from '@api/api/kg-insights';
import { Icon } from '@design/components/icon/Icon';
import { RevampTable, TableCell, TableHeader } from '@design/components/table';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { ThemeCell } from '@terminal/components/theme-cell/ThemeCell';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { ThemeOverviewDrawer } from '@terminal/widgets/theme-widget/theme-overview-drawer/ThemeOverviewDrawer';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PulseRelatedTheme } from '../../pulse-types';
import styles from './RelatedThemesTable.module.scss';

type TooltipLabelType = 'companiesCount' | 'positive' | 'negative' | 'mixed';

const TOOLTIP_LABEL_KEYS: Record<
  TooltipLabelType,
  { title: string; body: string }
> = {
  companiesCount: {
    title: 'widget:themeOverview.pulse.tooltipCompaniesCountTitle',
    body: 'widget:themeOverview.pulse.tooltipCompaniesCountBody',
  },
  positive: {
    title: 'widget:themeOverview.pulse.tooltipPositiveTitle',
    body: 'widget:themeOverview.pulse.tooltipPositiveBody',
  },
  negative: {
    title: 'widget:themeOverview.pulse.tooltipNegativeTitle',
    body: 'widget:themeOverview.pulse.tooltipNegativeBody',
  },
  mixed: {
    title: 'widget:themeOverview.pulse.tooltipMixedTitle',
    body: 'widget:themeOverview.pulse.tooltipMixedBody',
  },
};

const TooltipLabel = ({
  type,
  themeName,
}: {
  type: TooltipLabelType;
  themeName?: string;
}) => {
  const { t } = useTranslation(['widget']);
  const { title, body } = TOOLTIP_LABEL_KEYS[type];

  return (
    <div className={styles.tooltipLabel}>
      <span className={styles.tooltipTitle}>{t(title)}</span>
      <span>{t(body, { theme: themeName })}</span>
    </div>
  );
};

const ExposureCount = ({
  icon,
  count,
  tooltip,
}: {
  icon: ReactNode;
  count: number;
  tooltip: ReactNode;
}) => (
  <Tooltip label={tooltip} inPortal>
    <span className={styles.exposureCount}>
      {icon}
      {count}
    </span>
  </Tooltip>
);

export interface RelatedThemesTableProps {
  themes: PulseRelatedTheme[];
  themeName: string;
  themeColumnLabel: string;
  themeType: KgInsightThemeType;
  onThemeClick?: (themeId: string) => void;
  dataTestId?: string;
  isCompact?: boolean;
}

export const RelatedThemesTable = ({
  themes,
  themeName,
  themeColumnLabel,
  themeType,
  onThemeClick,
  dataTestId = 'pulse-related-themes-table',
  isCompact = false,
}: RelatedThemesTableProps) => {
  const { t } = useTranslation(['widget']);
  const [selectedThemeId, setSelectedThemeId] = useState<string>();
  const {
    trackThemeOverviewDrawerOpen,
    trackThemeOverviewRelatedThemesRowClicked,
  } = usePosthogEvents();
  const trackingSource = isCompact
    ? POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER
    : POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW;

  const handleOpenTheme = (themeId: string) => {
    trackThemeOverviewRelatedThemesRowClicked({
      source: trackingSource,
      themeIds: [themeId],
      type: themeType,
    });

    if (onThemeClick) {
      onThemeClick(themeId);
      return;
    }

    trackThemeOverviewDrawerOpen(POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW, [
      themeId,
    ]);
    setSelectedThemeId(themeId);
  };

  const handleCloseThemeDrawer = () => setSelectedThemeId(undefined);

  return (
    <>
      <div
        className={clsx(styles.tableWrapper, {
          [styles.isCompact]: isCompact,
        })}
      >
        <RevampTable className={styles.table} data-testid={dataTestId}>
          <thead>
            <tr>
              <TableHeader
                isHoverEnabled={false}
                alignment="start"
                className={clsx(styles.tableHeader, styles.rankCell)}
              >
                {t('widget:themeOverview.pulse.rank')}
              </TableHeader>
              <TableHeader
                isHoverEnabled={false}
                alignment="start"
                className={clsx(styles.tableHeader, styles.themeCell)}
              >
                {themeColumnLabel}
              </TableHeader>
              <TableHeader
                isHoverEnabled={false}
                alignment="end"
                className={styles.tableHeader}
              >
                <Tooltip
                  label={
                    <TooltipLabel type="companiesCount" themeName={themeName} />
                  }
                  inPortal
                >
                  <span>{t('widget:themeOverview.pulse.companiesCount')}</span>
                </Tooltip>
              </TableHeader>
              <TableHeader
                isHoverEnabled={false}
                alignment="start"
                className={clsx(styles.tableHeader, styles.exposureCell)}
              >
                {t('widget:themeOverview.pulse.exposureBreakdown')}
              </TableHeader>
            </tr>
          </thead>
          <tbody>
            {themes.map(theme => (
              <tr key={theme.themeId}>
                <TableCell
                  className={clsx(styles.tableCell, styles.rankCell)}
                  alignment="left"
                  onClick={() => handleOpenTheme(theme.themeId)}
                >
                  <TopRankIcon rank={theme.rank} />
                </TableCell>
                <TableCell
                  alignment="left"
                  className={clsx(styles.tableCell, styles.themeCell)}
                  isNumber={false}
                  onClick={() => handleOpenTheme(theme.themeId)}
                >
                  <ThemeCell themeId={theme.themeId} size={28} />
                </TableCell>
                <TableCell
                  alignment="right"
                  isNumber
                  className={styles.tableCell}
                  onClick={() => handleOpenTheme(theme.themeId)}
                >
                  {theme.companiesCount}
                </TableCell>
                <TableCell
                  alignment="left"
                  className={clsx(styles.tableCell, styles.exposureCell)}
                  onClick={() => handleOpenTheme(theme.themeId)}
                >
                  <span className={styles.exposureBreakdown}>
                    <ExposureCount
                      icon={
                        <Icon
                          iconName="ChevronFillUp"
                          size={16}
                          fillColor="var(--icon-success)"
                        />
                      }
                      count={theme.positiveCount}
                      tooltip={<TooltipLabel type="positive" />}
                    />
                    <ExposureCount
                      icon={
                        <Icon
                          iconName="ChevronFillDown"
                          size={16}
                          fillColor="var(--icon-critical)"
                        />
                      }
                      count={theme.negativeCount}
                      tooltip={<TooltipLabel type="negative" />}
                    />
                    <ExposureCount
                      icon={<span className={styles.mixedDash} />}
                      count={theme.mixedCount}
                      tooltip={<TooltipLabel type="mixed" />}
                    />
                  </span>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </RevampTable>
      </div>
      {onThemeClick ? null : (
        <ThemeOverviewDrawer
          isOpen={!!selectedThemeId}
          onClose={handleCloseThemeDrawer}
          themeId={selectedThemeId}
          trackingSource={POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW}
        />
      )}
    </>
  );
};
